import React from "react";

const MAX_ATTENTION_ITEMS = 6;
const MAX_OWNER_BUCKETS = 6;
const MAX_RECENT_CHANGES = 4;

const formatDate = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDue = (item) => {
  if (item?.next_test_due) return `Test ${formatDate(item.next_test_due)}`;
  if (item?.next_review_due) return `Review ${formatDate(item.next_review_due)}`;
  return "No due date";
};

const formatFilterPills = (filters, data) => {
  const pills = [];
  if (data?.as_of || filters?.asOf) pills.push(`As at: ${formatDate(data?.as_of || filters?.asOf)}`);
  if (filters?.search) pills.push(`Search: ${filters.search}`);
  if (filters?.status) pills.push(`Status: ${filters.status}`);
  if (filters?.owner) pills.push(`Owner: ${filters.owner}`);
  if (filters?.category) pills.push(`Category: ${filters.category}`);
  if (filters?.catalog) pills.push(`Catalog: ${filters.catalog}`);
  return pills.length > 0 ? pills : ["Current controls scope"];
};

export default function ControlsHealthPrintPreview({
  data,
  filters,
  screen = false,
  root = false,
  testId = "controls-health-print-preview",
}) {
  const kpis = [
    { key: "total", label: "Total", value: data?.total_controls ?? 0 },
    { key: "attention", label: "Attention", value: data?.attention_controls ?? 0 },
    { key: "ineffective", label: "Ineffective", value: data?.ineffective_controls ?? 0 },
    { key: "overdue_reviews", label: "Overdue reviews", value: data?.overdue_reviews ?? 0 },
    { key: "overdue_tests", label: "Overdue tests", value: data?.overdue_tests ?? 0 },
    { key: "missing_owner", label: "Missing owner", value: data?.missing_owner ?? 0 },
    { key: "missing_evidence", label: "Missing evidence", value: data?.missing_evidence ?? 0 },
    { key: "open_actions", label: "Open actions", value: data?.controls_with_open_actions ?? 0 },
  ];
  const attentionItems = Array.isArray(data?.attention_items) ? data.attention_items.slice(0, MAX_ATTENTION_ITEMS) : [];
  const hiddenAttentionCount = Math.max((data?.attention_items?.length || 0) - attentionItems.length, 0);
  const ownerBuckets = Array.isArray(data?.attention_by_owner) ? data.attention_by_owner.slice(0, MAX_OWNER_BUCKETS) : [];
  const hiddenOwnerCount = Math.max((data?.attention_by_owner?.length || 0) - ownerBuckets.length, 0);
  const recentChanges = Array.isArray(data?.recent_changes) ? data.recent_changes.slice(0, MAX_RECENT_CHANGES) : [];
  const hiddenRecentCount = Math.max((data?.recent_changes?.length || 0) - recentChanges.length, 0);
  const filterPills = formatFilterPills(filters, data);

  return (
    <div
      className={screen ? "controls-health-print-screen-preview" : undefined}
      data-controls-health-print-root={root ? "true" : undefined}
      data-testid={testId}
    >
      <article className="controls-health-print-sheet" data-print-layout="dedicated">
        <header className="controls-health-print-header">
          <div>
            <h1 className="controls-health-print-title">Controls health snapshot</h1>
            <div className="controls-health-print-subtitle">
              {data?.scope_note || "Showing metrics for controls matching the current filters."}
            </div>
          </div>
          <div className="controls-health-print-pill-row">
            {filterPills.map((pill) => (
              <span key={pill} className="controls-health-print-pill">
                {pill}
              </span>
            ))}
          </div>
        </header>

        <section className="controls-health-print-kpi-grid">
          {kpis.map((metric) => (
            <div key={metric.key} className="controls-health-print-card">
              <div className="controls-health-print-label">{metric.label}</div>
              <div className="controls-health-print-kpi-value">{metric.value}</div>
            </div>
          ))}
        </section>

        <section className="controls-health-print-card">
          <div className="controls-health-print-section-header">
            <div className="controls-health-print-section-title">Needs attention</div>
            <div className="controls-health-print-meta">
              {data?.attention_controls ?? 0} control{data?.attention_controls === 1 ? "" : "s"}
            </div>
          </div>
          {attentionItems.length > 0 ? (
            <>
              <table className="controls-health-print-table">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Owner</th>
                    <th>Reason</th>
                    <th>Due</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {attentionItems.map((item) => (
                    <tr key={item.control_id}>
                      <td>
                        <strong>{item.control_name}</strong>
                        <div>{item.control_identifier || `#${item.control_id}`}</div>
                      </td>
                      <td>{item.owner || "Unassigned"}</td>
                      <td>{Array.isArray(item.reasons) && item.reasons.length > 0 ? item.reasons.join(", ") : item.primary_reason}</td>
                      <td>{formatDue(item)}</td>
                      <td>
                        {(item.open_actions_count || 0) + (item.open_findings_count || 0) > 0
                          ? `${item.open_actions_count || 0}A / ${item.open_findings_count || 0}F`
                          : "None"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hiddenAttentionCount > 0 && (
                <div className="controls-health-print-meta">+ {hiddenAttentionCount} more attention items not shown</div>
              )}
            </>
          ) : (
            <div className="controls-health-print-empty">No controls currently need attention.</div>
          )}
        </section>

        <section className="controls-health-print-detail-grid">
          <div className="controls-health-print-card">
            <div className="controls-health-print-section-header">
              <div className="controls-health-print-section-title">Attention by owner</div>
            </div>
            {ownerBuckets.length > 0 ? (
              <div className="controls-health-print-list">
                {ownerBuckets.map((bucket) => (
                  <div key={bucket.owner} className="controls-health-print-list-row">
                    <span>{bucket.owner}</span>
                    <strong>{bucket.count}</strong>
                  </div>
                ))}
                {hiddenOwnerCount > 0 && (
                  <div className="controls-health-print-meta">+ {hiddenOwnerCount} more owners not shown</div>
                )}
              </div>
            ) : (
              <div className="controls-health-print-empty">No owner breakdown available.</div>
            )}
          </div>

          <div className="controls-health-print-card">
            <div className="controls-health-print-section-header">
              <div className="controls-health-print-section-title">Recently changed</div>
            </div>
            {recentChanges.length > 0 ? (
              <div className="controls-health-print-list">
                {recentChanges.map((item) => (
                  <div key={item.control_id} className="controls-health-print-list-row controls-health-print-list-row--stacked">
                    <span>
                      <strong>{item.control_name}</strong>
                      <div>{item.control_identifier || `#${item.control_id}`}</div>
                    </span>
                    <span>{formatDate(item.updated_at)}</span>
                  </div>
                ))}
                {hiddenRecentCount > 0 && (
                  <div className="controls-health-print-meta">+ {hiddenRecentCount} more recent changes not shown</div>
                )}
              </div>
            ) : (
              <div className="controls-health-print-empty">No recent changes to show.</div>
            )}
          </div>
        </section>

        {data?.limitation_note && <footer className="controls-health-print-footer">{data.limitation_note}</footer>}
      </article>
    </div>
  );
}
